import * as mongoose from 'mongoose';
import { modelOptions, prop, Severity } from '@typegoose/typegoose';

class License {
  @prop({ required: true })
  type: number;

  @prop({ required: false })
  productId: number;
}

export class CompanyShort {
  @prop({ required: true })
  id: mongoose.Types.ObjectId;

  @prop({ required: true })
  name: string;
}

class InheritedFrom {
  @prop({ required: false })
  id: mongoose.Types.ObjectId;
}

class AssignedPolicy {
  @prop({ required: true })
  policyId: mongoose.Types.ObjectId;

  @prop({ required: true })
  policyName: string;

  @prop({ required: true })
  inheritedFrom: InheritedFrom;

  @prop({ required: true })
  lastModified: Date;
}

class ActivePolicy {
  @prop({ required: true })
  policyName: string;

  @prop({ required: true })
  policyId: mongoose.Types.ObjectId;
}

class ClientPolicy {
  @prop({ required: true })
  policyId: mongoose.Types.ObjectId;

  @prop({ required: false })
  assignedOn?: Date;
}

class Rights {
  @prop({ required: true })
  companyManager: boolean;

  @prop({ required: true })
  manageCompanies: boolean;

  @prop({ required: true })
  manageNetworks: boolean;
}

class ProductService {
  @prop({ required: true })
  type: number;

  @prop({ required: true })
  status: number;

  @prop({ required: true })
  statusStartDate: Date;

  @prop({ required: true })
  statusEndDate?: Date;

  @prop({ required: true })
  activeSince?: Date;

  @prop({ required: true })
  used: number;

  @prop({ required: true })
  flavour: number;
}

class Features {
  @prop({ required: false })
  exchangeUsable?: boolean;

  @prop({ required: false })
  filelessAttackUsable?: boolean;

  @prop({ required: false })
  paranoidUsable?: boolean;

  @prop({ required: false })
  storageProtectionUsable?: boolean;

  @prop({ required: false })
  containerProtectionUsable?: boolean;

  @prop({ required: false })
  earlyAccess?: boolean;

  @prop({ required: false })
  remoteAccess?: boolean;

  @prop({ required: false })
  mdrAdviceUsable?: boolean;

  @prop({ required: false })
  tagsVisible?: boolean;

  @prop({ required: false })
  iorVisible?: boolean;

  @prop({ required: false })
  iorUsable?: boolean;

  @prop({ required: false })
  compliancePremiumUsable?: boolean;
}

class FeatureHistory {
  @prop({ required: false })
  setToTrue?: number;

  @prop({ required: false })
  setToFalse?: number;
}

class FeaturesWithHistory {
  @prop({ required: false, _id: false })
  exchangeUsable?: FeatureHistory;

  @prop({ required: false, _id: false })
  filelessAttackUsable?: FeatureHistory;

  @prop({ required: false, _id: false })
  paranoidUsable?: FeatureHistory;

  @prop({ required: false, _id: false })
  storageProtectionUsable?: FeatureHistory;

  @prop({ required: false, _id: false })
  containerProtectionUsable?: FeatureHistory;
}

class MDR {
  @prop({ required: false })
  remoteAccess?: boolean;
}

class TargetedFeatures {
  @prop({ required: false })
  mdr?: MDR;
}

class PolicyInfo {
  @prop({ required: true })
  id: mongoose.Types.ObjectId;
}

class RuleInfo {
  @prop({ required: true })
  id: mongoose.Types.ObjectId;
}

class ClientRule {
  @prop({ required: true, type: PolicyInfo })
  policy: PolicyInfo;

  @prop({ required: true, type: RuleInfo })
  rule: RuleInfo;

  @prop({ required: false })
  appliedOn?: Date;
}

class EndpointUsers {
  @prop({ required: true })
  userNames: Array<string>;

  @prop({ required: true })
  userCount: number;

  @prop({ required: true })
  lastUpdate: Date;
}

class Specifics {
  @prop({ required: true })
  isManaged: boolean;

  @prop({ required: true })
  lastSync: Date;

  @prop({ required: true })
  isOnline: boolean;

  @prop({ required: false })
  fqdn?: string;

  @prop({ required: false })
  operatingSystemVersion?: string;

  @prop({ required: false })
  label?: string;

  @prop({ required: false })
  ip?: string;

  @prop({ required: false })
  uuid?: string;

  @prop({ required: false })
  imageName?: string;

  @prop({ required: false })
  endpointId?: mongoose.Types.ObjectId;

  @prop({ required: false })
  operatingSystem?: number;

  @prop({ required: false })
  isVirtual?: boolean;

  @prop({ required: false })
  isContainerHost?: boolean;

  @prop({ required: false })
  isVirtualServer?: boolean;

  @prop({ required: false })
  inheritedLicense?: boolean;

  @prop({ required: false, type: [License] })
  licenses?: Array<License>;

  @prop({ required: false })
  rights?: Rights;

  @prop({ required: true, _id: false })
  features: Features;

  @prop({ required: false })
  targetedFeatures: TargetedFeatures;

  @prop({ required: false, _id: false })
  featuresWithHistory: FeaturesWithHistory;

  @prop({ required: false })
  maEnabled?: boolean;

  @prop({ required: false, type: [ProductService] })
  productServices?: Array<ProductService>;

  @prop({ required: false })
  isIsolated?: boolean;

  @prop({ required: false })
  restorePath?: string;

  @prop({ required: false })
  isPowerUser?: boolean;

  @prop({ required: false, type: [ClientRule] })
  clientRules?: Array<ClientRule>;

  @prop({ required: true })
  appId: number;

  @prop({ required: false })
  clientPolicy?: ClientPolicy;

  @prop({ required: false })
  logoFileId?: mongoose.Types.ObjectId;

  @prop({ required: false })
  hasPatchManagement?: boolean;

  @prop({ required: false, type: [mongoose.Types.ObjectId] })
  gzTagIds?: Array<mongoose.Types.ObjectId>;

  @prop({ required: false })
  endpointUsers?: EndpointUsers;
}

@modelOptions({
  schemaOptions: { collection: 'protectedEntities', strict: false },
  options: {
    allowMixed: Severity.ALLOW,
    customName: 'protectedEntities',
  },
})
export class ProtectedEntities {
  @prop({ required: true, _id: true })
  _id: mongoose.Types.ObjectId;

  @prop({ required: true, _id: false, type: Specifics })
  specifics: Specifics;

  @prop({ required: true })
  name: string;

  @prop({ required: true })
  type: number;

  @prop({ required: true, _id: false, type: CompanyShort })
  company: CompanyShort | null;

  @prop({ required: false })
  assignedPolicy?: AssignedPolicy;

  @prop({ required: false })
  activePolicy?: ActivePolicy;
}
